import {
  useGetGridStatusQuery,
  useGridTickNowMutation,
  usePatchSettingsMutation,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { GridSymbolPanel } from "@/components/domain/GridSymbolPanel";
import { GridAdoptedOrphans } from "@/components/domain/GridAdoptedOrphans";

export default function GridOverview() {
  const status = useGetGridStatusQuery(undefined, { pollingInterval: 30000 });
  const [tick, tickState] = useGridTickNowMutation();
  const [patch, patchState] = usePatchSettingsMutation();
  const [busy, setBusy] = useState(false);

  const s: any = status.data || {};
  const enabled = !!s.enabled;
  const liveEnabled = !!s.live_enabled;

  async function toggleEnabled() {
    setBusy(true);
    try { await patch({ grid_trader_enabled: !enabled } as any).unwrap(); }
    finally { setBusy(false); }
  }
  async function toggleLive() {
    if (!liveEnabled && !confirm(
      "Enable LIVE grid trading? Real USDT/BTC will be used. " +
      "Make sure paper results are good first.")) return;
    setBusy(true);
    try { await patch({ grid_trader_live_enabled: !liveEnabled } as any).unwrap(); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">
            Grid Trader
            <span className="text-muted font-mono text-base ml-2">
              · {(s.symbols || [s.symbol || "BTC/USDT"]).join(" · ")}
            </span>
          </h1>
          <p className="text-sm text-muted max-w-2xl">
            Multi-symbol range-bound grid bot. ATR-adaptive spacing,
            EMA200/ADX/BB regime gating, Kelly-capped sizing. Each symbol
            runs an independent grid with an equal slice of paper equity
            (${s.per_symbol_equity ?? "?"} per symbol). Gemini Flash consults
            on borderline regimes.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" disabled={busy || patchState.isLoading}
                  onClick={toggleEnabled}>
            {enabled ? "Disable engine" : "Enable engine"}
          </Button>
          <Button variant={liveEnabled ? "danger" : "ghost"} size="sm"
                  disabled={busy || patchState.isLoading || !enabled}
                  onClick={toggleLive}>
            {liveEnabled ? "Stop live" : "Go live"}
          </Button>
          <Button size="sm" disabled={tickState.isLoading || !enabled}
                  onClick={() => tick()}>
            Tick now
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label={liveEnabled ? "Engine (LIVE)" : "Engine"} value={enabled ? "ON" : "OFF"}
              tone={enabled ? (liveEnabled ? "live" : "paper") : "off"}
              sub={s.bypass_regime ? "bypass regime ON" : undefined} />
        <Tile label="Preset" value={presetLabel(s.preset)}
              sub={`tick every ${s.tick_interval_minutes ?? 15}m`} />
        <Tile label={liveEnabled ? "Live wallet equity" : "Total paper equity"}
              value={`$${Number(s.effective_total_equity_usdt ?? s.paper_equity_usdt ?? 1000).toLocaleString(undefined, {maximumFractionDigits: 2})}`}
              sub={`$${Number(s.per_symbol_equity ?? 0).toFixed(2)}/symbol`}
              tone={liveEnabled ? "live" : undefined} />
        <Tile label="Aggregated 30d"
              value={`${Number(s.aggregated_realized_pnl_usd ?? 0) >= 0 ? "+" : ""}$${Number(s.aggregated_realized_pnl_usd ?? 0).toFixed(2)}`}
              sub={`${s.aggregated_fills_count ?? 0} fills across all symbols`}
              tone={Number(s.aggregated_realized_pnl_usd ?? 0) > 0 ? "good" : Number(s.aggregated_realized_pnl_usd ?? 0) < 0 ? "bad" : "muted"} />
      </div>

      {/* Per-symbol panels — expandable card per symbol with full chart
          (price line + grid level reference lines + live price marker +
          fill scatter dots) and two tables (open orders / executed fills). */}
      {s.by_symbol && Object.keys(s.by_symbol).length > 0 && (
        <div className="space-y-3">
          {Object.entries(s.by_symbol).map(([sym, info]: [string, any]) => (
            <GridSymbolPanel
              key={sym}
              symbol={sym}
              regime={info.regime}
              fillsSummary={info.fills_summary}
              defaultExpanded={info.regime?.verdict === "grid_friendly"}
            />
          ))}
        </div>
      )}

      {/* Adopted orphan orders — secondary visibility into open orders that
          survive on non-active runs / carry the grid_level=-1 sentinel.
          Renders nothing when there are none. */}
      <GridAdoptedOrphans symbols={(s.symbols as string[]) ?? []} />

      {tickState.data && (
        <Card>
          <CardHeader><CardTitle className="text-base">Last manual tick</CardTitle></CardHeader>
          <CardContent>
            <pre className="text-xs overflow-x-auto">{JSON.stringify(tickState.data, null, 2)}</pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const PRESET_LABELS: Record<string, string> = {
  conservative: "Conservative",
  moderate: "Moderate",
  aggressive: "Aggressive",
  paxg_tuned: "PAXG Tuned",
};
function presetLabel(p?: string) {
  if (!p) return "—";
  return PRESET_LABELS[p] || p.replace(/_/g, " ");
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  const cls = tone === "good" ? "border-emerald-500/30" : tone === "bad" ? "border-rose-500/30"
    : tone === "warn" ? "border-amber-500/30" : tone === "live" ? "border-rose-500/40"
    : tone === "paper" ? "border-emerald-500/30" : "border-border";
  return (
    <div className={`rounded-lg border ${cls} bg-bg2 p-3`}>
      <div className="text-xs text-muted">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
      {sub && <div className="text-xs text-muted">{sub}</div>}
    </div>
  );
}

